import {
  Component,
  OnInit,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  OnChanges, SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '@root/src/environments/environment';

declare var L: any;

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: `./map.component.html`,
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit, AfterViewInit,  OnChanges {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;
  
  @Input() height = '100%';
  @Input() center: [number, number] = [environment.map.lat, environment.map.lng]; 
  @Input() zoom = 13;
  @Input() drawingMode = false;

  @Output() coordinatesChange = new EventEmitter<{ lat: number; lng: number }[]>();
  @Output() markerClick = new EventEmitter<{ lat: number; lng: number }>();
  @Output() mapClick = new EventEmitter<{ lat: number; lng: number }>();
  
  private map: any;
  private centerMarker: any = null;
  mapLoaded = false;

  ngOnInit() {
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    if (typeof L === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => this.initializeMap();
      document.body.appendChild(script);
    }
  }

  ngAfterViewInit() {
    if (typeof L !== 'undefined') {
      setTimeout(() => this.initializeMap(), 100);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['center'] && this.map) {

      const coords = changes['center'].currentValue as [number, number];

      setTimeout(() => {
        this.map.panTo(coords, this.map.getZoom()); 
        this.updateCenterMarker(coords[0], coords[1]);
      }, 250)
    }
  }

  private initializeMap() {
    if (!this.mapContainer || this.map) return;

    try {
      this.map = L.map(this.mapContainer.nativeElement, {
        doubleClickZoom: true,
        scrollWheelZoom: true,
        zoomControl: true,
        attributionControl: false,
        wheelDebounceTime: 40,
        wheelPxPerZoomLevel: 60
      }).setView(this.center, this.zoom);

      setTimeout(() => {
        if (this.map.scrollWheelZoom) {
          this.map.scrollWheelZoom.enable();
          this.map.panTo(this.center, this.zoom);
        }
      }, 100);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: ''
      }).addTo(this.map);

      let isDragging = false;
      let dragTimeout: any;
      
      this.map.on('movestart', () => {
        isDragging = true;
        clearTimeout(dragTimeout);
      });
      
      this.map.on('moveend', () => {
        dragTimeout = setTimeout(() => {
          isDragging = false;
        }, 200);
      });

      this.updateCenterMarker(this.center[0], this.center[1]);
      
      this.map.on('click', (e: any) => {
        if (isDragging) return;
        if (!e.originalEvent || e.originalEvent.type !== 'click') return;
        
        this.mapClick.emit({ lat: e.latlng.lat, lng: e.latlng.lng });
      });

      this.mapLoaded = true;
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }

  
  public updateCenterMarker(lat: number, lng: number) {
    if (this.map) {
      if (this.centerMarker) {
        this.map.removeLayer(this.centerMarker);
      }
      
      this.centerMarker = L.marker([lat, lng], {
        icon: L.icon({
          iconUrl: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 2.902 1.004 5.57 2.68 7.682L12.5 41l9.82-20.818C23.996 18.07 25 15.402 25 12.5 25 5.596 19.404 0 12.5 0z" fill="#ff0000"/>
              <circle cx="12.5" cy="12.5" r="6" fill="white"/>
            </svg>
          `),
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34]
        })
      }).addTo(this.map);
    }
  }
  
  public clearCenterMarker() {
    if (this.centerMarker && this.map) {
      this.map.removeLayer(this.centerMarker);
      this.centerMarker = null;
    }
  }
}